import {
  Button,
  Kbd,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCheck, PiTarget } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { formatVesselName } from '@ais-ui/utils';
import { ShipInfo } from '@ais-ui/websockets';
import { useTargetChooser } from './contexts/TargetChooserContext';
import { useSelectionState } from '../../components/SelectionStateProvider';

interface ChooseTargetCardProps {
  /** Grid placement style, shared with the follow-ship indicator so both
   * overlays sit in the same bottom-centre slot of the map panel grid. */
  style?: React.CSSProperties;
}

/** Human-readable prompt per requested target type. Falls back to a generic
 * label so new target types degrade gracefully without code changes here. */
const TARGET_TITLES: Record<string, string> = {
  ship: 'Select a vessel',
};

const resolveTitle = (targetTypes: string[]): string => {
  const match = targetTypes.map((type) => TARGET_TITLES[type]).find(Boolean);
  return match ?? 'Select a target';
};

export const ChooseTargetCard = ({ style }: ChooseTargetCardProps) => {
  const {
    onChooseTarget,
    onRejectTarget,
    chooseTargetTypes,
    showChooseTargetPanel,
  } = useTargetChooser();

  const { targetInfoDetailsList } = useSelectionState();

  const isAllowedTargetTypeSelected = useMemo(() => {
    if (targetInfoDetailsList.length === 0) {
      return false;
    }

    return chooseTargetTypes.includes(
      targetInfoDetailsList[0].extractedTargetInfoType,
    );
  }, [chooseTargetTypes, targetInfoDetailsList]);

  const title = useMemo(() => {
    if (isAllowedTargetTypeSelected) {
      const selected = targetInfoDetailsList[0].extractedTargetInfo[0];
      const name = formatVesselName((selected as ShipInfo).name);
      return name ? `Selected vessel ${name}` : resolveTitle(chooseTargetTypes);
    }
    return resolveTitle(chooseTargetTypes);
  }, [isAllowedTargetTypeSelected, targetInfoDetailsList, chooseTargetTypes]);

  const finishButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && finishButtonRef.current) {
        finishButtonRef.current.click();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  if (
    !onChooseTarget.current ||
    !onRejectTarget.current ||
    !showChooseTargetPanel
  ) {
    return null;
  }

  const handleChoose = () => {
    if (onChooseTarget.current) {
      onChooseTarget.current(targetInfoDetailsList[0].extractedTargetInfo[0]);
    } else {
      console.error('onChooseTarget is not available');
    }
  };

  return (
    <div
      style={style}
      className="flex justify-center items-end pb-4 pointer-events-none"
    >
      <div className="flex items-center gap-5 bg-background border rounded-2xl shadow-md px-5 py-4 pointer-events-auto">
        <div className="flex items-center justify-center size-12 shrink-0 rounded-full bg-primary/10 text-primary">
          <PiTarget className="size-6" />
        </div>
        <div className="w-56 space-y-0.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            Choose target
          </p>
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          <p className="text-sm text-muted-foreground">
            Click a target on the map to populate your data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            id="choosetarget-reject-button"
            onClick={onRejectTarget.current}
            variant="outline"
          >
            <XIcon className="size-4" />
            Reject
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                ref={finishButtonRef}
                id="choosetarget-finish-button"
                disabled={!isAllowedTargetTypeSelected}
                onClick={handleChoose}
              >
                <PiCheck className="size-4" />
                Choose
              </Button>
            </TooltipTrigger>
            <TooltipContent className="flex space-x-2">
              Finish, use <Kbd className="text-xs mx-2 p-0">Enter</Kbd>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
